import { ColDef, ColumnState, GridApi, RowClassParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { debounce } from "lodash";
import {
	aggridHeaderClass,
	commonBorderStyle,
	commonBottomRowStyle,
	commonCodeCellStyle,
	commonNumberCellStyle,
	commonRowIndexCellStyle,
	commonTextCellStyle,
	pdaCellStyle
} from "src/components/common/common";
import { stringComparator } from "../string/stringUtil";


export const getRowStyleFlags = (params: any, options: {
	deletedList: string[];
	selectedList: string[];
	dataList: any[];
}) => {
	const { deletedList, selectedList, dataList } = options;

	// console.log('params.data.valueIndex: ' + JSON.stringify(params.data));
	// console.log('params.data.valueIndex: ' + JSON.stringify(params.data['valueIndex']));

	const pinned = params.node.rowPinned; 
	if(pinned === 'bottom') return { isHovered: false, isDeleted: false, isSelected: false, isInserted: false, isModified: false, isTransparent: false };

	const isDeleted = params.data?.['valueIndex'] && deletedList.includes(params.data['valueIndex']);
	const isSelected = params.data?.['valueIndex'] && selectedList.includes(params.data['valueIndex']);
	const isInserted = dataList.find(value => value.valueIndex === params.data?.['valueIndex'])?.inserted === true;
	const isModified = dataList.find(value => value.valueIndex === params.data?.['valueIndex'])?.isModified === true && !isInserted;
	const isTransparent = isDeleted || isSelected || isInserted || isModified;
	return { isDeleted, isSelected, isInserted, isModified, isTransparent };	
};

export const cellStyleMap = {
	rowIndex: () => ({ 
		...commonBorderStyle, 
		...commonRowIndexCellStyle,
		...pdaCellStyle,
		justifyContent: 'center',
	}),
	default: (isTransparent: boolean) => ({
		...commonBorderStyle,
		backgroundColor: isTransparent ? 'transparent' : '#F0F0F0'
	})
} as const;

// 스타일 타입 정의
export type CellStyleType = keyof typeof cellStyleMap; 

//detail table은 개별 rowClassRules 생성하기로 한다.

// 행 스타일(선택/hover 등) 규칙
// 공통 rowClassRules 생성 함수
// T는 compCd를 가지고 있는 Dto 아무거나라면 된다
// selectedItem은 스토어에서 선언한 그리드 내의 선택된 행을 지칭할 객체이다
export const rowClassRules_compCd = <T extends { compCd: string }>(
	selectedItem: T | undefined
): Record<string, (params: RowClassParams<T>) => boolean> => {
	// console.log('selectedItem : ', selectedItem);
	return {
		'selected': (params) => {
			if (params.node.rowPinned === 'bottom') return false;
			// console.log('params.data.compCd : ', params.data?.compCd);
			return selectedItem !== undefined && params.data?.compCd === selectedItem?.compCd;
		},
		'hover': (params) => {
			if (params.node.rowPinned === 'bottom') return false;
			return !(selectedItem !== undefined && params.data?.compCd === selectedItem?.compCd) && true;
		}
	};
};

export const rowClassRules_itemCd = <T extends { itemCd: string }>(
	selectedItem: T | undefined
): Record<string, (params: RowClassParams<T>) => boolean> => {
	return {
		'selected': (params) => selectedItem !== undefined && params.data?.itemCd === selectedItem?.itemCd,
		'hover': (params) => !(selectedItem !== undefined && params.data?.itemCd === selectedItem?.itemCd) && true
	}
};

export const rowClassRules_majorCd = <T extends { majorCd: string, minorCd: string, inserted?: boolean, isModified?: boolean }>(
	selectedMajorCode: T | undefined,
	selectedMinorCodeList: string[],
	deletedMinorCodeList: string[],
	isMajor: boolean
): Record<string, (params: RowClassParams<T>) => boolean> => {
	return {
		'selected': (params) => {
			if (params.node.rowPinned === 'bottom') return false;
			return isMajor ?
			selectedMajorCode?.majorCd === params.data?.majorCd :
			selectedMinorCodeList.includes(params.data?.minorCd || '');
		},
		'deleted': (params) => {
			if (params.node.rowPinned === 'bottom') return false;
			return deletedMinorCodeList.includes(params.data?.minorCd || '');
		},
		'inserted': (params) => {
			if (params.node.rowPinned === 'bottom') return false;
			return params.data?.inserted ?? false;
		},
		'modified': (params) => {
			if (params.node.rowPinned === 'bottom') return false;
			return params.data?.isModified ?? false;
		},
		'hover': (params) => {
			if (params.node.rowPinned === 'bottom') return false;
			return !(isMajor ?
			selectedMajorCode?.majorCd === params.data?.majorCd :
			selectedMinorCodeList.includes(params.data?.minorCd || '')) && true;
		}
	};
};

// 그리드 준비 시 컬럼 상태 복원
export const onGridReady = (
	gridRef: React.RefObject<AgGridReact | null>,
	COLUMN_STATE_KEY: string,
	setColumnState: (state: ColumnState[]) => void,
	getDefaultColumnState?: () => ColumnState[],
) => {
	const saved = localStorage.getItem(COLUMN_STATE_KEY);
	if (saved) {
		const parsed = JSON.parse(saved);
		if (parsed.length === 0) return;
		setColumnState(parsed);
		gridRef.current?.api.applyColumnState({
			state: parsed,
			applyOrder: true,
		});
		return;
	}

	if (getDefaultColumnState) {
		const defaultState = getDefaultColumnState();
		if (defaultState.length === 0) return;
		setColumnState(defaultState);
		gridRef.current?.api.applyColumnState({
			state: defaultState,
			applyOrder: true,
		});
		localStorage.setItem(COLUMN_STATE_KEY, JSON.stringify(defaultState));
		return;
	}

	// 컬럼 상태가 없을 때도 기본 상태 설정하여 AG-Grid 완전 초기화
	setTimeout(() => {
		if (gridRef.current?.api) {
			const defaultState = gridRef.current.api.getColumnState();
			if (defaultState && defaultState.length > 0) {
				setColumnState(defaultState);
			}
		}
	}, 100);
};

// 컬럼 이동/정렬 등 변경 시 상태 저장 (debounce 적용)
export const createSaveColumnState = (
	gridRef: React.RefObject<AgGridReact | null>, 
	COLUMN_STATE_KEY: string, 
	setColumnState: (state: ColumnState[]) => void
) => {
	return debounce(() => {
		const state = gridRef.current?.api.getColumnState();
		if (state) {
			if (state.length === 0) return;
			// rowIndex 컬럼을 포함한 전체 상태를 저장하도록 수정
			localStorage.setItem(COLUMN_STATE_KEY, JSON.stringify(state));
			setColumnState(state);
		}
	}, 100);
}
/**
 * Ag Grid API로 해당 그리드 인스턴스의 body viewport DOM을 조회합니다.
 */
export const getGridBodyViewport = (api: GridApi): HTMLElement | null => {
	const internalViewport = (api as unknown as { gridBodyCtrl?: { eBodyViewport?: HTMLElement } })
		.gridBodyCtrl?.eBodyViewport;
	if (internalViewport) return internalViewport;

	const gridId = api.getGridId();
	const root = document.querySelector(`[grid-id="${gridId}"]`);
	return root?.querySelector('.ag-body-viewport') as HTMLElement | null ?? null;
};

/**
 * 그리드 세로 스크롤 위치(픽셀)를 Store에 저장합니다.
 * Grid_Touch의 onBodyScrollEnd에서 호출되며, 탭 전환 후 재마운트 시 복원에 사용됩니다.
 * @param api Ag Grid API
 * @param setScrollTop 화면별 Store의 setScrollTop
 */
export const saveGridScrollState = (
	api: GridApi,
	setScrollTop: (top: number | null) => void
) => {
	const scrollTop = api.getVerticalPixelRange().top;
	if (scrollTop >= 0) {
		setScrollTop(scrollTop);
	}
};

/**
 * Store에 저장된 스크롤 픽셀 위치로 그리드를 복원합니다.
 * Grid_Touch의 onFirstDataRendered에서 호출되며, body viewport scrollTop을 직접 설정합니다.
 * scrollTop이 null/undefined이면 복원하지 않습니다(조회·초기화 직후 등).
 * @param api Ag Grid API
 * @param scrollTop Store에 저장된 getVerticalPixelRange().top 값
 * @param onComplete 스크롤 적용 완료 후 콜백 (로딩 스피너 해제 등)
 */
export const restoreGridScrollState = (
	api: GridApi,
	scrollTop: number | null | undefined,
	onComplete?: () => void
) => {
	if (scrollTop == null || scrollTop < 0) {
		onComplete?.();
		return;
	}

	const applyScroll = () => {
		const viewport = getGridBodyViewport(api);
		if (viewport) {
			viewport.scrollTop = scrollTop;
		}
	};

	requestAnimationFrame(() => {
		applyScroll();
		requestAnimationFrame(() => {
			applyScroll();
			onComplete?.();
		});
	});
};

/** 동시에 복원 중인 그리드 수 (탭 복귀 시 대·상세 그리드 동시 복원 대응) */
let scrollRestoreLoadingCount = 0;

/**
 * 스크롤 복원 구간에 전역 로딩 스피너를 표시합니다. setLoadingSpinner 먼저 시행후 success 반응시 다음 로직 수행
 * 복원이 모두 끝나면 스피너를 해제합니다.
 */
export const withScrollRestoreLoading = (
	setLoadingSpinner: (loading: boolean) => void,
	runRestore: (onComplete: () => void) => void,
) => {
	scrollRestoreLoadingCount += 1;
	if (scrollRestoreLoadingCount === 1) {
		setLoadingSpinner(true);
	}
	runRestore(() => {
		scrollRestoreLoadingCount = Math.max(0, scrollRestoreLoadingCount - 1);
		if (scrollRestoreLoadingCount === 0) {
			setLoadingSpinner(false);
		}
	});
};

export const getRowStyle = (params: any) => {
	if (params.node.rowPinned === 'bottom') return commonBottomRowStyle;
	// 새 {} 를 매 호출 반환하면 AG Grid setStylesFromGridOptions → React setState 무한 루프 발생
	return undefined;
}

export const defaultGridCellBorderClass = 'border-r-2 border-gray-300';

export const defaultColDef = {
	sortable: true,
	resizable: true,
	cellClass: defaultGridCellBorderClass,
	comparator: stringComparator,
	wrapText: false,
	autoHeight: false,
	headerClass: aggridHeaderClass,
	suppressSynchronization: true,
	// 숫자 에디터에서 ArrowUp/ArrowDown에 의한 값 증감을 전역 차원에서 차단
	suppressKeyboardEvent: (params: any) => {
		const key = params.event?.key;
		
		// 'ArrowDown'이 항상 true로 평가되던 버그를 수정합니다.
		if (params.editing && (key === 'ArrowUp' || key === 'ArrowDown')) {
			params.event.preventDefault();
			return true; // 편집 중 ↑, ↓ 키만 차단
		}
		return false; // 그 외 모든 키 이벤트는 허용
	},
}

export const getDefaultRowIndexColumnConfig = (
	cellRenderer: (params: any) => any = (params: any) => {
		if (params.node.rowPinned === 'bottom') return '';
		return params.node.rowIndex + 1;
	}
) => {
	return [{
		headerName: '',  colId: 'rowIndex', width: 60,
		cellStyle: cellStyleMap.rowIndex(),
		cellClass: (params: any) => {
			if (params.data && params.data.inserted === true) {
				return 'inserted-cell';
			}
			if (params.data && params.data.isModified === true) {
				return 'modified-cell';
			}
			if (params.data && params.data.isDeleted === true) {
				return 'deleted-cell';
			}
			return '';
		},
		sortable: false, suppressMovable: true,
		resizable: false,
		
		cellRenderer,
	}];
};

/** 초기 로드 시 왼쪽 고정 (사용자가 헤더 메뉴로 pin 변경 가능) */
export const withInitialPinnedLeftColumn = <T extends ColDef<any>>(col: T): T => ({
	...col,
	pinned: "left",
	suppressMovable: true,
});

/** Excel 틀 고정처럼 컬럼을 왼쪽에 고정 — lockPinned로 UI/API pin 변경 불가 */
export const withPinnedLeftColumn = <T extends ColDef<any>>(col: T): T => ({
	...col,
	pinned: "left",
	lockPinned: true,
	suppressMovable: true,
});


export const IndexParameterCell = () => {
	return [{
		headerName: 'valueIndex', field: 'valueIndex', width: 500, 
		cellStyle: commonBorderStyle, editable: false,
		
		cellRenderer: (params: any) => {
			if (params.node.rowPinned === 'bottom') return '';
			return (
				''
				+ ' ValueIndex : ' + params.data?.valueIndex 
				+ ' OrderIndex : ' + params.data?.orderIndex 
				+ ' OrderIndexSeq : ' + params.data?.orderIndexSeq 
				// + ' Inserted : ' + params.data?.inserted 
				// + ' IsModified : ' + params.data?.isModified
			);
		}
	}] as const;
}


// 추가: 왼쪽 정렬 강제 컬럼이면 false 반환
export const validateLeftAlignColumns = (colId: string, alignLeftColumns: string[]) => {
	return alignLeftColumns?.includes(colId);
}

// 순수 숫자이거나, 숫자로 변환 가능한 문자열(쉼표 포함)인 경우 오른쪽 정렬로 간주하여 center는 false 반환
export const validateNumberLike = (value: any) => {
	return typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value.replace(/,/g, ''))));
}
